/* eslint-disable @typescript-eslint/no-empty-interface */
/* eslint-disable jsx-a11y/media-has-caption */
import {
  Avatar as ChakraAvatar,
  Flex,
  Icon as ChakraIcon,
  IconButton,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import {
  IonContent,
  IonHeader,
  IonPage,
  IonProgressBar,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BsChatDotsFill } from 'react-icons/bs';
import { FaPlusCircle } from 'react-icons/fa';
import { FiArrowLeft, FiArrowRight, FiX } from 'react-icons/fi';
import { RiListCheck } from 'react-icons/ri';
import { RouteComponentProps } from 'react-router';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Avatar } from 'rsuite';
import shallow from 'zustand/shallow';
import { EpisodeSection } from '../@types';
import { useCurrentUser, useEpisode, useSonarGroups } from '../api';
import BlgButton from '../components/Button/Button';
import * as Constants from '../constants';
import { EpisodeDashboard, EpisodeWrapper } from '../features/episode';
import { useEpisodeStore } from '../store';
import './Tab1.css';
import { EpisodeChat } from '../features/episode/components/EpisodeChat';
import { BlgModalSonarPlus } from '../components/ModalSonar/ModalSonarPlus';
import { BlgModalEpisode } from '../components/ModalEpisode/ModalEpisode';
import { HeaderToolbar } from '../features/toolbar';

const PageHover = styled.div`
  height: 100%;
  width: 100%;
  .dashboard-btn {
    background: var(--ion-color-primary);
    position: absolute;
    z-index: 200;
    left: 0;
    right: 0;
    width: 100px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto;
    color: #fff;
    opacity: 0;
    transition: 0.6s;
    pointer-events: none;
  }
  &.hovered {
    .dashboard-btn {
      opacity: 1;
      pointer-events: auto;
    }
  }
`;
interface EpisodeTabProps
  extends RouteComponentProps<{
    episode_id: string;
  }> {}
const EpisodeTab: React.FC<EpisodeTabProps> = ({ match }) => {
  const isIframe = window.location !== window.parent.location;
  const [isHovered, setHovered] = useState(false);
  const hoverTimeoutRef = useRef<any>(null);
  const location = useLocation();
  const history = useHistory();
  const { data: currentUser, isLoading: userLoading } = useCurrentUser();
  const { data: episode, isLoading } = useEpisode(match.params.episode_id);
  const {
    data: { active: groups, pending } = {},
    isLoading: sonarLoading,
  } = useSonarGroups(match.params.episode_id, currentUser?.id || '');

  const numAlerts =
    (pending?.length || 0) +
    (groups || []).reduce(
      (sum: number, g: Record<string, any>) => sum + g.unread_count,
      0
    );
  const {
    isOpen: isOpenSonar,
    onOpen: onOpenSonar,
    onClose: onCloseSonar,
  } = useDisclosure();
  const {
    isOpen: isOpenChat,
    onOpen: onOpenChat,
    onClose: onCloseChat,
  } = useDisclosure();
  const {
    isOpen: isOpenInfos,
    onOpen: onOpenInfos,
    onClose: onCloseInfos,
  } = useDisclosure();

  const currentSectionFromQuery = useMemo(
    () => Number(new URLSearchParams(location.search).get('part') || 1) - 1,
    [location.search]
  );

  const shouldOpenSonar = useMemo(
    () => new URLSearchParams(location.search).get('openSonar') === '1',
    [location.search]
  );

  const {
    showEndScreen,
    showSonar,
    showDashboard,
    EPISODE_ACTIONS,
  } = useEpisodeStore(
    (state) => ({
      EPISODE_ACTIONS: state.EPISODE_ACTIONS,
      showEndScreen: state.showEndScreen,
      showDashboard: state.showDashboard,
      showSonar: state.showSonar,
    }),
    shallow
  );

  const [currentSection, setCurrentSection]: [
    EpisodeSection | undefined,
    any
  ] = useState(episode?.sections?.[currentSectionFromQuery]);

  const handleNextEpisode = () => {};

  const handleHovered = () => {
    clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(function () {
      setHovered(false);
    }, 2000);
    setHovered(true);
  };

  useEffect(() => {
    EPISODE_ACTIONS.setShowDashboard(true);
    if (episode) {
      const newSectionIdx =
        episode.sections.length >= currentSectionFromQuery + 1
          ? currentSectionFromQuery
          : 0;
      let newUrl = `?part=${newSectionIdx + 1}`;
      if (shouldOpenSonar && !showSonar) {
        newUrl += '&openSonar=1';
        EPISODE_ACTIONS.setShowSonar(true);
        onOpenChat();
      }
      const fullUrl = `${window.location.href.replace(
        window.location.search,
        ''
      )}${newUrl}`;

      if (fullUrl !== window.location.href) history.replace(newUrl);
      setCurrentSection(episode.sections?.[newSectionIdx]);
    }
  }, [
    episode?.sections?.length,
    currentSectionFromQuery,
    shouldOpenSonar,
    showSonar,
  ]);

  if (!episode || userLoading) return null;
  return (
    <IonPage>
      {episode && (
        <PageHover
          onMouseEnter={handleHovered}
          onMouseMove={handleHovered}
          className={isHovered ? 'hovered' : ''}
        >
          <EpisodeDashboard
            setShowDashboard={EPISODE_ACTIONS.setShowDashboard}
            {...(currentSection && {
              sectionState: [
                episode.sections,
                currentSection,
                setCurrentSection,
              ],
            })}
            episode={episode}
            currentUser={currentUser}
            show={showDashboard}
          />

          <IonHeader
            onMouseEnter={() => setHovered(true)}
            className={isHovered ? 'hovered' : ''}
          >
            <Flex
              mt={1}
              px={6}
              py={4}
              position="relative"
              justify="space-between"
            >
              <Flex justify="space-between" gap={2}>
                {!isIframe && (
                  <BlgButton
                    as="a"
                    href={`${Constants.ROOT_URL}/explore`}
                    colorScheme="gray"
                    color="black"
                    leftIcon={<FiArrowLeft />}
                  >
                    Library
                  </BlgButton>
                )}

                <BlgButton
                  variant="solid"
                  colorScheme="ligthBlue"
                  color="black"
                  leftIcon={<FiArrowLeft />}
                  fontWeight={700}
                  as="a"
                  target={isIframe ? '_top' : '_self'}
                  href={`${Constants.ROOT_URL}/series/${episode.series.slug}`}
                >
                  Series
                </BlgButton>
                <Flex>
                  {currentUser && currentUser.user_role_id === 2 && (
                    <>
                      {currentUser.access_level === 2 && (
                        <IconButton
                          aria-label="ListCheck"
                          icon={<RiListCheck />}
                          fontSize={20}
                          borderRadius="full"
                          bg="transparent"
                          colorScheme="whiteAlpha"
                          onClick={onOpenInfos}
                        />
                      )}
                      <IconButton
                        aria-label="ListCheck"
                        icon={<FaPlusCircle />}
                        fontSize={20}
                        borderRadius="full"
                        bg="transparent"
                        colorScheme="whiteAlpha"
                        onClick={onOpenSonar}
                      />
                    </>
                  )}
                  <Flex
                    borderRadius="full"
                    w={10}
                    h={10}
                    justifyContent="center"
                    position="relative"
                  >
                    {numAlerts > 0 && (
                      <Flex
                        position="absolute"
                        top={1}
                        right={-0.5}
                        w={4}
                        h={4}
                        zIndex={2}
                        bg="red.500"
                        borderRadius="full"
                        justifyContent="center"
                        alignItems="center"
                      >
                        <Text fontSize={10} color="white" fontWeight={700}>
                          {numAlerts}
                        </Text>
                      </Flex>
                    )}
                    <IconButton
                      aria-label="ListCheck"
                      icon={<BsChatDotsFill />}
                      fontSize={20}
                      borderRadius="full"
                      bg="transparent"
                      colorScheme="whiteAlpha"
                      onClick={onOpenChat}
                    />
                  </Flex>
                </Flex>
              </Flex>
              <Flex alignItems="center" gap={4} mr={8}>
                {!isIframe && currentUser && (
                  <>
                    <ChakraAvatar h={10} w={10} src={currentUser?.avatar} />
                    <Text fontSize={14} fontWeight={600} color="white">
                      {currentUser?.username}
                    </Text>
                    <BlgButton
                      leftIcon={<FiArrowRight />}
                      variant="solid"
                      colorScheme="newGreen"
                      fontWeight={700}
                      as="a"
                      href={`${Constants.ROOT_URL}/users/logout`}
                    >
                      Log out
                    </BlgButton>
                  </>
                )}
                <IconButton
                  aria-label="Close"
                  bg="transparent"
                  color="white"
                  borderRadius="full"
                  onClick={() =>
                    EPISODE_ACTIONS.setShowDashboard(!showDashboard)
                  }
                  icon={<FiX />}
                />
              </Flex>
            </Flex>
            {/* <Button
              className="dashboard-btn"
              colorScheme="primary"
              borderTopRadius="0"
              p={6}
              onClick={() => EPISODE_ACTIONS.setShowDashboard(!showDashboard)}
            >
              <Icon size="2x" icon="web" />
            </Button> */}
            {/* <IconButton
              aria-label="Close"
              icon={<FiX />}
              m={4}
              variant="ghost"
              float="inline-end"
              onClick={() => EPISODE_ACTIONS.setShowDashboard(!showDashboard)}
            /> */}
            <IonToolbar className="episode-toolbar">
              <IonTitle className="episode-title">
                {/* <div className="episode-subtitle">Episode 1</div> */}
                <div>
                  <Avatar size="sm" circle src={episode.creator.logo_url} />
                  <span>{episode.title}</span>
                </div>
                {/* <IonButton
                  type="button"
                  className="btn btn-next-episode"
                  onClick={(e) => handleNextEpisode()}
                >
                  Next Episode
                  <Icon icon="long-arrow-right" />
                </IonButton> */}
              </IonTitle>
              {/* currentUser && (
                <HeaderToolbar episodeId={episode.id} user={currentUser} />
              ) */}
            </IonToolbar>
          </IonHeader>
          <IonContent fullscreen>
            <IonHeader collapse="condense">
              <IonToolbar>
                <IonTitle size="large">Tab 1</IonTitle>
              </IonToolbar>
            </IonHeader>
            {isLoading && (
              <div>
                <IonProgressBar type="indeterminate" />
              </div>
            )}
            {episode && currentUser && currentSection && !isLoading && (
              <EpisodeWrapper
                episode={episode}
                currentSection={currentSection}
              />
            )}
          </IonContent>
        </PageHover>
      )}
      {isOpenInfos && (
        <BlgModalEpisode
          episode={episode}
          isOpen={isOpenInfos}
          onClose={onCloseInfos}
        />
      )}
      {isOpenChat && currentUser && (
        <EpisodeChat
          isOpen={isOpenChat}
          isLoading={sonarLoading}
          sonarGroups={[groups, pending]}
          user={currentUser}
          episodeId={episode.id}
          onClose={onCloseChat}
        />
      )}
      {isOpenSonar && (
        <BlgModalSonarPlus
          isOpen={isOpenSonar}
          onClose={onCloseSonar}
          episode={episode}
        />
      )}
    </IonPage>
  );
};

export default EpisodeTab;
